import './rw-wonen-search.scss';

/**
 * rw-wonen-search — fixed bottom filter panel.
 * - Toggles open/close on the "Filters (X)" button.
 * - Counts active filter inputs (excluding sort/orderby) and updates the badge.
 * - Closes when clicking outside the panel.
 * - Reset clears filter fields (honouring preserve / no-reset) then LiveForm refresh.
 * - Panel open state survives AJAX fragment replace via sessionStorage.
 */
let documentBindingsInitialized = false;

function panelStorageKey( form ) {
	const id = form && form.id ? form.id : 'default';
	return 'rwWonenSearch:panelOpen:' + id + ':' + window.location.pathname;
}

function setOpenState( root, open ) {
	const toggle = root.querySelector( '[data-rw-search-toggle]' );
	const panel  = root.querySelector( '[data-rw-search-panel]' );

	if ( ! toggle || ! panel ) {
		return;
	}

	root.classList.toggle( '-open', open );
	toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );

	if ( open ) {
		panel.removeAttribute( 'hidden' );
	} else {
		panel.setAttribute( 'hidden', '' );
	}

	const form = root.closest( 'form' );
	if ( form ) {
		try {
			sessionStorage.setItem( panelStorageKey( form ), open ? '1' : '0' );
		} catch ( e ) {
			// Private mode or quota: ignore.
		}
	}
}

function bindDocumentEvents() {
	if ( documentBindingsInitialized ) {
		return;
	}

	documentBindingsInitialized = true;

	document.addEventListener( 'pointerdown', ( event ) => {
		const openRoots = document.querySelectorAll( '[data-component="rw-wonen-search"].-open' );
		openRoots.forEach( ( root ) => {
			if ( root.contains( event.target ) ) {
				return;
			}

			setOpenState( root, false );
		} );
	}, true );

	document.addEventListener( 'keydown', ( event ) => {
		if ( event.key !== 'Escape' ) {
			return;
		}

		const openRoot = document.querySelector( '[data-component="rw-wonen-search"].-open' );

		if ( ! openRoot ) {
			return;
		}

		setOpenState( openRoot, false );

		const toggle = openRoot.querySelector( '[data-rw-search-toggle]' );
		if ( toggle ) {
			toggle.focus();
		}
	} );
}

function fieldParticipatesInReset( field ) {
	if ( ! field.name ) {
		return false;
	}

	if ( field.closest( '[data-rw-search-preserve]' ) ) {
		return false;
	}

	if ( field.hasAttribute( 'data-rw-search-no-reset' ) ) {
		return false;
	}

	const type = ( field.type || '' ).toLowerCase();

	if ( type === 'hidden' ) {
		return false;
	}

	if ( type === 'submit' || type === 'button' || type === 'file' || type === 'reset' ) {
		return false;
	}

	if ( field.disabled ) {
		return false;
	}

	return true;
}

function resetFilterFields( form ) {
	const fields = form.querySelectorAll( 'select, textarea, input' );

	fields.forEach( ( field ) => {
		if ( ! fieldParticipatesInReset( field ) ) {
			return;
		}

		const tag = field.tagName;

		if ( tag === 'SELECT' ) {
			const selectEl = field;

			if ( selectEl.multiple ) {
				Array.from( selectEl.options ).forEach( ( opt ) => {
					opt.selected = false;
				} );
				return;
			}

			const opts     = selectEl.options;
			let emptyIndex = -1;

			for ( let i = 0; i < opts.length; i++ ) {
				if ( opts[ i ].value === '' ) {
					emptyIndex = i;
					break;
				}
			}

			selectEl.selectedIndex = emptyIndex >= 0 ? emptyIndex : 0;
			return;
		}

		const type = ( field.type || '' ).toLowerCase();

		if ( type === 'checkbox' || type === 'radio' ) {
			field.checked = false;
			return;
		}

		field.value = '';
	} );
}

function countActiveFilters( form ) {
	if ( ! form ) {
		return 0;
	}

	let active = 0;
	const fields = form.querySelectorAll( '[name]:not([data-rw-search-ignore-count])' );

	fields.forEach( ( field ) => {
		const name = field.getAttribute( 'name' );

		if ( ! name || 'orderby' === name ) {
			return;
		}

		if ( field.type === 'checkbox' || field.type === 'radio' ) {
			if ( field.checked ) {
				active++;
			}
			return;
		}

		if ( field.tagName === 'SELECT' ) {
			const option = field.options[ field.selectedIndex ];

			if ( option && option.value !== '' ) {
				active++;
			}
			return;
		}

		if ( field.value && field.value.trim() !== '' ) {
			active++;
		}
	} );

	return active;
}

function updateAllRwWonenSearchCounts() {
	document.querySelectorAll( '[data-component="rw-wonen-search"]' ).forEach( ( root ) => {
		const count = root.querySelector( '[data-rw-search-count]' );
		const reset = root.querySelector( '[data-rw-search-reset]' );
		const form  = root.closest( 'form' );

		if ( ! count || ! form ) {
			return;
		}

		const active = countActiveFilters( form );
		count.textContent = '(' + active + ')';

		if ( reset ) {
			if ( active > 0 ) {
				reset.removeAttribute( 'hidden' );
			} else {
				reset.setAttribute( 'hidden', '' );
			}
		}
	} );
}

function initRwWonenSearch() {
	const roots = document.querySelectorAll( '[data-component="rw-wonen-search"]' );

	if ( ! roots.length ) {
		return;
	}

	bindDocumentEvents();

	roots.forEach( ( root ) => {
		if ( root.dataset.rwSearchInitialized === 'true' ) {
			return;
		}

		const toggle = root.querySelector( '[data-rw-search-toggle]' );
		const count  = root.querySelector( '[data-rw-search-count]' );
		const reset  = root.querySelector( '[data-rw-search-reset]' );
		const form   = root.closest( 'form' );

		if ( ! toggle ) {
			return;
		}

		root.dataset.rwSearchInitialized = 'true';

		toggle.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			const isOpen = root.classList.contains( '-open' );
			setOpenState( root, ! isOpen );
		} );

		const updateCount = () => {
			if ( ! count || ! form ) {
				return;
			}

			const active = countActiveFilters( form );
			count.textContent = '(' + active + ')';

			if ( reset ) {
				if ( active > 0 ) {
					reset.removeAttribute( 'hidden' );
				} else {
					reset.setAttribute( 'hidden', '' );
				}
			}
		};

		if ( reset && form ) {
			reset.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				resetFilterFields( form );
				updateCount();
				document.dispatchEvent( new CustomEvent( 'rw-wonen-search:request-refresh' ) );
			} );
		}

		if ( form ) {
			form.addEventListener( 'change', updateCount );
			form.addEventListener( 'input', updateCount );
		}

		let storedOpen = false;

		if ( form ) {
			try {
				storedOpen = sessionStorage.getItem( panelStorageKey( form ) ) === '1';
			} catch ( e ) {
				storedOpen = false;
			}
		}

		const serverOpen = root.classList.contains( '-open' );
		setOpenState( root, serverOpen || storedOpen );
		updateCount();
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initRwWonenSearch );
} else {
	initRwWonenSearch();
}

document.addEventListener( 'rw-wonen-search:refresh', () => {
	initRwWonenSearch();
	updateAllRwWonenSearchCounts();
} );
